import { P2GAlert, P2GBox, P2GGrid, P2GTextField } from '@parcel2go/core-ui';
import { P2GButton } from '@parcel2go/core-ui';
import { P2GText } from '@parcel2go/core-ui';
import { P2GLink } from '@parcel2go/core-ui';
import { SvgEnquiryMoreInfo } from '@parcel2go/core-ui';
import { useEffect, useMemo } from 'react';
import { useState } from 'react';
import { Country, InputRuleApiModel, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { SmartSendItemGroup } from '../item/SmartSendGroup';
import SidePanelSection from '../../../../../components/sidepanel-section';
import { GenerateQuantityConvertedSummary } from './GroupingHelper';
import { QuantitySummary } from '../panels/QuantitySummary';
import { splitValueAcross, splitWeightByUnit } from '~/apps/smartsend/lib/parcels/allocate';
import { ParcelCountPicker } from '../panels/ParcelCountPicker';
import { parcelCountBound } from '~/apps/smartsend/lib/parcels/bounds';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import AlertWithIcon from '../../AlertWithIcon';
import { toDisplayCurrency } from '~/components/currency/toDisplayCurrency';
import ConsignmentSummaryParcels from '../panels/consignments/ConsignmentSummaryParcels';
import StickyButtons from '~/components/sticky';

export interface props {
  group: SmartSendItemGroup;
  split: (item: SmartSendItemApiModel, newItems: SmartSendItemApiModel[]) => void;
  countries?: Country[];
  productValidation?: InputRuleApiModel[];
  tariffSystem?: string;
  cancel?: () => void;
}

interface Box {
  quantity: number;
  value: number;
  weight: number;
}

export const Convert: React.FC<props> = (props) => {
  const mainOl = props.group?.Items[0]?.MarketplaceOrderLines[0];
  const mainDims = props.group?.Items[0]?.PackageDimensions;
  const [parcelCount, setParcelCount] = useState(Math.min(2, Number(mainOl?.Quantity) || 1));
  const [boxes, setBoxes] = useState<Box[]>([]);
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  const [preview, setPreview] = useState(true);

  function createSplitItems() {
    const summary = GenerateQuantityConvertedSummary(props.group, boxes);
    if (summary) {
      props.split(summary.item, summary.newItems);
    }
  }

  // Value and weight are distributed by DIFFERENT rules, deliberately.
  //
  // Value splits across the boxes' CURRENT total quantity (not mainOl.Quantity), so the box values always
  // sum to exactly mainOl.Value regardless of how the user has redistributed quantity between boxes - these
  // are customs declaration values and must not drift from a rounding shortfall. splitValueAcross is the
  // shared implementation of exactly this, and rounds each result. The inline copy that used to be here did
  // not, so a total that did not divide cleanly produced values like 100.00000000000001 in a customs field.
  //
  // Weight is per-unit against the ORIGINAL quantity, so it does not conserve: shipping three of four
  // ordered units really does weigh three quarters of the original, and the price should say so. See
  // splitWeightByUnit. Computed here rather than at commit time so the preview below and
  // GenerateQuantityConvertedSummary cannot disagree - the same reason the value lives here.
  function distributeBoxes(quantities: number[]): Box[] {
    const values = splitValueAcross(mainOl.Value, quantities);
    const weights = splitWeightByUnit(mainDims?.Weight, mainOl.Quantity, quantities);
    return quantities.map((quantity, i) => ({ quantity, value: values[i], weight: weights[i] }));
  }

  useEffect(() => {
    const counts = QuantitySummary(mainOl.Quantity, parcelCount);
    setBoxes(distributeBoxes(counts));
  }, [mainOl?.Quantity, parcelCount]);

  const quantityMismatch = useMemo(
    () => boxes.length > 0 && boxes.reduce((acc, b) => acc + Number(b.quantity), 0) !== mainOl?.Quantity,
    [boxes, mainOl?.Quantity]
  );

  const hasInvalidQuantity = useMemo(
    () => boxes.some((b) => !Number.isInteger(b.quantity) || b.quantity <= 0),
    [boxes]
  );

  function updateBoxQuantity(index: number, quantity: number) {
    const quantities = boxes.map((b, i) => (i === index ? quantity : b.quantity));
    setBoxes(distributeBoxes(quantities));
  }

  if (!mainOl) {
    return null;
  }

  const currencyCode = props.group.Items[0]?.CurrencyCode;

  return (
    <P2GBox>
      <P2GBox mb={3}>
        <P2GText variant="body2">
          <Trans
            t={t}
            i18nKey="SPLIT_ITEM_MULTI_QUANTITY_INTRO_TEXT"
            values={{
              itemQuantity: mainOl.Quantity,
              currency: currencyCode,
              itemDescription: mainOl.Product.Description
            }}
            defaults={`This will allow you to split your '<strong>{{itemQuantity}} x {{itemDescription}}</strong>' across multiple parcels so they can be collected at different times or on different services.`}
          />
        </P2GText>
      </P2GBox>
      <P2GBox mb={3}>
        <ParcelCountPicker
          availableParcels={parcelCountBound('clamp', mainOl.Quantity)}
          selected={parcelCount}
          justify="flex-start"
          onSelect={setParcelCount}
          helperText={null}
        />
      </P2GBox>
      {parcelCount > 1 ? (
        <>
          {quantityMismatch && (
            <P2GBox mb={3}>
              <AlertWithIcon color="warning" icon={<SvgEnquiryMoreInfo />}>
                <P2GText variant="body2">
                  {t('QUANTITY_MISMATCH_WARNING', {
                    defaultValue:
                      'The quantities entered below no longer add up to the {{itemQuantity}} originally ordered. You can still continue.',
                    itemQuantity: mainOl.Quantity
                  })}
                </P2GText>
              </AlertWithIcon>
            </P2GBox>
          )}
          <P2GBox>
            <SidePanelSection title={t('SHIPMENT_SUMMARY_SUBTITLE', { defaultValue: 'Shipment Summary' })}>
              {preview ? (
                <ConsignmentSummaryParcels
                  currencyCode={currencyCode}
                  setExpand={() => setPreview(false)}
                  consignment={{
                    Parcels: boxes.map((x, i) => {
                      return {
                        Contents: [
                          {
                            Description: mainOl.Product.Description,
                            Quantity: x.quantity,
                            Value: x.value,
                            Sku: mainOl.Product.Sku,
                            CountryOfManufacture: mainOl.Product.CountryOfManufacture,
                            Id: i.toString(),
                            UID: i.toString()
                          }
                        ],
                        CanAdd: false,
                        // Per-box weight, not the item's total - showing mainDims here told the customer
                        // every box weighed the full original.
                        Dimensions: { ...mainDims, Weight: x.weight },
                        Id: i.toString(),
                        UID: i.toString()
                      };
                    })
                  }}
                />
              ) : (
                <P2GBox sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {boxes.map((box, i) => (
                    <P2GGrid key={i} container spacing={2} alignItems="center">
                      <P2GGrid item xs={12} sm={5}>
                        <P2GText>
                          <strong>
                            {t('BOX_NUMBER_LABEL', {
                              defaultValue: 'Box {{number}} of {{total}}',
                              number: i + 1,
                              total: boxes.length
                            })}
                          </strong>
                        </P2GText>
                      </P2GGrid>
                      <P2GGrid item xs={6} sm={3}>
                        <P2GTextField
                          fullWidth
                          type="number"
                          min={1}
                          label={t('QUANTITY_INPUT_LABEL', { defaultValue: 'Quantity' })}
                          id={`quantity-${i}`}
                          value={String(box.quantity)}
                          onChange={(v) => updateBoxQuantity(i, Number(v.currentTarget.value))}
                        />
                      </P2GGrid>
                      <P2GGrid item xs={6} sm={4}>
                        <P2GTextField
                          fullWidth
                          disabled
                          label={t('VALUE_INPUT_LABEL', { defaultValue: 'Value' })}
                          id={`value-${i}`}
                          value={toDisplayCurrency(currencyCode, box.value)}
                          onChange={() => {}}
                        />
                      </P2GGrid>
                    </P2GGrid>
                  ))}
                </P2GBox>
              )}
            </SidePanelSection>
          </P2GBox>
        </>
      ) : (
        <AlertWithIcon color="warning" icon={<SvgEnquiryMoreInfo />}>
          <P2GText variant="body2">
            {t('SHIPMENT_COUNT_TOO_LOW_WARNING', {
              defaultValue: 'Please select a number of shipments greater than 1 to split this order.'
            })}{' '}
            <P2GLink onClick={() => props.cancel?.()}>{t('CANCEL_LINK', { defaultValue: 'Cancel' })}</P2GLink>
          </P2GText>
        </AlertWithIcon>
      )}
      {parcelCount > 1 && (
        <P2GBox mt={2}>
          <P2GAlert type="success">
            <P2GText variant="body2">
              {t('CONVERT_SUMMARY_TEXT', {
                defaultValue: 'This will create {{shipmentCount}} shipments from the {{totalQuantity}} ordered.',
                shipmentCount: boxes.length,
                totalQuantity: mainOl.Quantity
              })}
            </P2GText>
          </P2GAlert>
        </P2GBox>
      )}
      <StickyButtons>
        <P2GBox mt={2}>
          <P2GButton variant="primary" disabled={parcelCount <= 1 || hasInvalidQuantity} onClick={createSplitItems}>
            {t('CONFIRM_CREATE_BUTTON', { defaultValue: 'Confirm and Create' })}
          </P2GButton>
        </P2GBox>
      </StickyButtons>
    </P2GBox>
  );
};
